/**
 * Scheduling API Service
 */

import { apiClient } from '../config';
import type {
  Zone,
  Segment,
  SegmentItem,
  Sequence,
  SequenceItem,
  Playlist,
  PlaylistItem,
  Schedule,
  ScheduleEntry,
  Daypart,
  EnergyCurve,
  SchedulingDashboard,
  WeekSchedule,
  ScheduleGap,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
  message?: string;
}

export class SchedulingService {
  // =========================================================================
  // Zones
  // =========================================================================

  static async listZones(params?: {
    organization_id?: string;
    site_id?: string;
    is_active?: boolean;
  }): Promise<Zone[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.site_id) queryParams.set('site_id', params.site_id);
    if (params?.is_active !== undefined) queryParams.set('is_active', String(params.is_active));

    const response = await apiClient.get<ApiResponse<Zone[]>>(
      `/api/scheduling/zones?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch zones');
    return response.data.data;
  }

  static async getZone(id: string): Promise<Zone> {
    const response = await apiClient.get<ApiResponse<Zone>>(`/api/scheduling/zones/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch zone');
    return response.data.data;
  }

  static async createZone(data: Partial<Zone>): Promise<Zone> {
    const response = await apiClient.post<ApiResponse<Zone>>('/api/scheduling/zones', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create zone');
    return response.data.data;
  }

  static async updateZone(id: string, data: Partial<Zone>): Promise<Zone> {
    const response = await apiClient.put<ApiResponse<Zone>>(`/api/scheduling/zones/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update zone');
    return response.data.data;
  }

  static async deleteZone(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/scheduling/zones/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete zone');
  }

  static async duplicateZone(zoneId: string, name?: string, targetSiteId?: string): Promise<Zone & { schedules_copied: number; entries_copied: number }> {
    const response = await apiClient.post<ApiResponse<Zone & { schedules_copied: number; entries_copied: number }>>(
      `/api/scheduling/zones/${zoneId}/duplicate`,
      { name, target_site_id: targetSiteId }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to duplicate zone');
    return response.data.data;
  }

  static async getZoneNowPlaying(id: string): Promise<ScheduleEntry | null> {
    const response = await apiClient.get<ApiResponse<ScheduleEntry | null>>(
      `/api/scheduling/zones/${id}/now-playing`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch now playing');
    return response.data.data;
  }

  // =========================================================================
  // Segments
  // =========================================================================

  static async listSegments(params?: {
    organization_id?: string;
    search?: string;
  }): Promise<Segment[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.search) queryParams.set('search', params.search);

    const response = await apiClient.get<ApiResponse<Segment[]>>(
      `/api/scheduling/segments?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch segments');
    return response.data.data;
  }

  static async getSegment(id: string): Promise<Segment> {
    const response = await apiClient.get<ApiResponse<Segment>>(`/api/scheduling/segments/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch segment');
    return response.data.data;
  }

  static async createSegment(data: Partial<Segment>): Promise<Segment> {
    const response = await apiClient.post<ApiResponse<Segment>>('/api/scheduling/segments', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create segment');
    return response.data.data;
  }

  static async updateSegment(id: string, data: Partial<Segment>): Promise<Segment> {
    const response = await apiClient.put<ApiResponse<Segment>>(`/api/scheduling/segments/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update segment');
    return response.data.data;
  }

  static async deleteSegment(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/scheduling/segments/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete segment');
  }

  static async addTrackToSegment(
    segmentId: string,
    contentId: string,
    position?: number,
    transitionType?: string
  ): Promise<SegmentItem> {
    const response = await apiClient.post<ApiResponse<SegmentItem>>(
      `/api/scheduling/segments/${segmentId}/items`,
      { content_id: contentId, position, transition_type: transitionType }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add track');
    return response.data.data;
  }

  static async removeTrackFromSegment(segmentId: string, itemId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/scheduling/segments/${segmentId}/items/${itemId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove track');
  }

  static async reorderSegmentTracks(segmentId: string, itemIds: string[]): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/scheduling/segments/${segmentId}/reorder`,
      { item_ids: itemIds }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to reorder tracks');
  }

  static async autoFillSegment(segmentId: string, maxTracks?: number): Promise<SegmentItem[]> {
    const response = await apiClient.post<ApiResponse<SegmentItem[]>>(
      `/api/scheduling/segments/${segmentId}/auto-fill`,
      { max_tracks: maxTracks }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to auto-fill segment');
    return response.data.data;
  }

  static async analyzeSegment(segmentId: string): Promise<{
    target: {
      track_count: number;
      bpm_min: number | null;
      bpm_max: number | null;
      energy_level: number | null;
    };
    actual: {
      track_count: number;
      total_duration_seconds: number;
      avg_bpm: number | null;
      bpm_range: string | null;
      avg_energy: number | null;
      energy_range: string | null;
    };
    issues: string[];
    meets_criteria: boolean;
  }> {
    const response = await apiClient.get<ApiResponse<any>>(
      `/api/scheduling/segments/${segmentId}/analyze`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to analyze segment');
    return response.data.data;
  }

  static async duplicateSegment(segmentId: string, name?: string, targetOrganizationId?: string): Promise<Segment> {
    const response = await apiClient.post<ApiResponse<Segment>>(
      `/api/scheduling/segments/${segmentId}/duplicate`,
      { name, target_organization_id: targetOrganizationId }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to duplicate segment');
    return response.data.data;
  }

  // =========================================================================
  // Sequences
  // =========================================================================

  static async listSequences(params?: {
    organization_id?: string;
    search?: string;
  }): Promise<Sequence[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.search) queryParams.set('search', params.search);

    const response = await apiClient.get<ApiResponse<Sequence[]>>(
      `/api/scheduling/sequences?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch sequences');
    return response.data.data;
  }

  static async getSequence(id: string): Promise<Sequence> {
    const response = await apiClient.get<ApiResponse<Sequence>>(`/api/scheduling/sequences/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch sequence');
    return response.data.data;
  }

  static async createSequence(data: Partial<Sequence>): Promise<Sequence> {
    const response = await apiClient.post<ApiResponse<Sequence>>('/api/scheduling/sequences', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create sequence');
    return response.data.data;
  }

  static async updateSequence(id: string, data: Partial<Sequence>): Promise<Sequence> {
    const response = await apiClient.put<ApiResponse<Sequence>>(`/api/scheduling/sequences/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update sequence');
    return response.data.data;
  }

  static async deleteSequence(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/scheduling/sequences/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete sequence');
  }

  static async addSegmentToSequence(sequenceId: string, segmentId: string, position?: number): Promise<SequenceItem> {
    const response = await apiClient.post<ApiResponse<SequenceItem>>(
      `/api/scheduling/sequences/${sequenceId}/items`,
      { segment_id: segmentId, position }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add segment');
    return response.data.data;
  }

  static async removeSegmentFromSequence(sequenceId: string, itemId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/scheduling/sequences/${sequenceId}/items/${itemId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove segment');
  }

  static async reorderSequenceSegments(sequenceId: string, itemIds: string[]): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/scheduling/sequences/${sequenceId}/reorder`,
      { item_ids: itemIds }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to reorder segments');
  }

  static async previewSequence(sequenceId: string): Promise<{
    sequence_id: string;
    sequence_name: string;
    total_duration_seconds: number;
    segment_count: number;
    energy_curve: Array<{ time: number; energy: number; segment: string; color: string }>;
    segments: Array<{
      position: number;
      duration_override_minutes: number | null;
      name: string;
      energy_level: number | null;
      avg_energy: number | null;
      actual_duration_seconds: number | null;
      color: string | null;
    }>;
  }> {
    const response = await apiClient.get<ApiResponse<any>>(
      `/api/scheduling/sequences/${sequenceId}/preview`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to preview sequence');
    return response.data.data;
  }

  static async duplicateSequence(sequenceId: string, name?: string, targetOrganizationId?: string): Promise<Sequence> {
    const response = await apiClient.post<ApiResponse<Sequence>>(
      `/api/scheduling/sequences/${sequenceId}/duplicate`,
      { name, target_organization_id: targetOrganizationId }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to duplicate sequence');
    return response.data.data;
  }

  // =========================================================================
  // Playlists
  // =========================================================================

  static async listPlaylists(params?: {
    organization_id?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<Playlist[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.is_active !== undefined) queryParams.set('is_active', String(params.is_active));
    if (params?.search) queryParams.set('search', params.search);

    const response = await apiClient.get<ApiResponse<Playlist[]>>(
      `/api/scheduling/playlists?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch playlists');
    return response.data.data;
  }

  static async getPlaylist(id: string): Promise<Playlist> {
    const response = await apiClient.get<ApiResponse<Playlist>>(`/api/scheduling/playlists/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch playlist');
    return response.data.data;
  }

  static async createPlaylist(data: Partial<Playlist>): Promise<Playlist> {
    const response = await apiClient.post<ApiResponse<Playlist>>('/api/scheduling/playlists', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create playlist');
    return response.data.data;
  }

  static async updatePlaylist(id: string, data: Partial<Playlist>): Promise<Playlist> {
    const response = await apiClient.put<ApiResponse<Playlist>>(`/api/scheduling/playlists/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update playlist');
    return response.data.data;
  }

  static async deletePlaylist(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/scheduling/playlists/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete playlist');
  }

  static async addTrackToPlaylist(playlistId: string, contentId: string, position?: number): Promise<PlaylistItem> {
    const response = await apiClient.post<ApiResponse<PlaylistItem>>(
      `/api/scheduling/playlists/${playlistId}/items`,
      { content_id: contentId, position }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add track');
    return response.data.data;
  }

  static async removeTrackFromPlaylist(playlistId: string, itemId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/scheduling/playlists/${playlistId}/items/${itemId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove track');
  }

  static async reorderPlaylistTracks(playlistId: string, itemIds: string[]): Promise<void> {
    const response = await apiClient.post<ApiResponse<void>>(
      `/api/scheduling/playlists/${playlistId}/reorder`,
      { item_ids: itemIds }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to reorder tracks');
  }

  static async duplicatePlaylist(playlistId: string, name?: string, targetOrganizationId?: string): Promise<Playlist> {
    const response = await apiClient.post<ApiResponse<Playlist>>(
      `/api/scheduling/playlists/${playlistId}/duplicate`,
      { name, target_organization_id: targetOrganizationId }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to duplicate playlist');
    return response.data.data;
  }

  // =========================================================================
  // Schedules
  // =========================================================================

  static async listSchedules(params?: {
    organization_id?: string;
    zone_id?: string;
    is_active?: boolean;
  }): Promise<Schedule[]> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.zone_id) queryParams.set('zone_id', params.zone_id);
    if (params?.is_active !== undefined) queryParams.set('is_active', String(params.is_active));

    const response = await apiClient.get<ApiResponse<Schedule[]>>(
      `/api/scheduling/schedules?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch schedules');
    return response.data.data;
  }

  static async getSchedule(id: string): Promise<Schedule> {
    const response = await apiClient.get<ApiResponse<Schedule>>(`/api/scheduling/schedules/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch schedule');
    return response.data.data;
  }

  static async createSchedule(data: Partial<Schedule>): Promise<Schedule> {
    const response = await apiClient.post<ApiResponse<Schedule>>('/api/scheduling/schedules', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create schedule');
    return response.data.data;
  }

  static async updateSchedule(id: string, data: Partial<Schedule>): Promise<Schedule> {
    const response = await apiClient.put<ApiResponse<Schedule>>(`/api/scheduling/schedules/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update schedule');
    return response.data.data;
  }

  static async deleteSchedule(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/scheduling/schedules/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete schedule');
  }

  static async addScheduleEntry(scheduleId: string, data: Partial<ScheduleEntry>): Promise<ScheduleEntry> {
    const response = await apiClient.post<ApiResponse<ScheduleEntry>>(
      `/api/scheduling/schedules/${scheduleId}/entries`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add entry');
    return response.data.data;
  }

  static async updateScheduleEntry(
    scheduleId: string,
    entryId: string,
    data: Partial<ScheduleEntry>
  ): Promise<ScheduleEntry> {
    const response = await apiClient.put<ApiResponse<ScheduleEntry>>(
      `/api/scheduling/schedules/${scheduleId}/entries/${entryId}`,
      data
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update entry');
    return response.data.data;
  }

  static async deleteScheduleEntry(scheduleId: string, entryId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(
      `/api/scheduling/schedules/${scheduleId}/entries/${entryId}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete entry');
  }

  static async getScheduleGaps(scheduleId: string): Promise<ScheduleGap[]> {
    const response = await apiClient.get<ApiResponse<ScheduleGap[]>>(
      `/api/scheduling/schedules/${scheduleId}/gaps`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to get gaps');
    return response.data.data;
  }

  static async cloneScheduleDay(
    scheduleId: string,
    sourceDay: number,
    targetDays: number[]
  ): Promise<{ cloned_count: number }> {
    const response = await apiClient.post<ApiResponse<{ cloned_count: number }>>(
      `/api/scheduling/schedules/${scheduleId}/clone-day`,
      { source_day: sourceDay, target_days: targetDays }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to clone day');
    return response.data.data;
  }

  // =========================================================================
  // Energy Curves & Dayparts
  // =========================================================================

  static async listEnergyCurves(organizationId?: string): Promise<EnergyCurve[]> {
    const queryParams = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await apiClient.get<ApiResponse<EnergyCurve[]>>(
      `/api/scheduling/energy-curves${queryParams}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch energy curves');
    return response.data.data;
  }

  static async listDayparts(organizationId?: string): Promise<Daypart[]> {
    const queryParams = organizationId ? `?organization_id=${organizationId}` : '';
    const response = await apiClient.get<ApiResponse<Daypart[]>>(
      `/api/scheduling/dayparts${queryParams}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch dayparts');
    return response.data.data;
  }

  // =========================================================================
  // Dashboard
  // =========================================================================

  static async getDashboardNowPlaying(params?: {
    organization_id?: string;
    site_id?: string;
  }): Promise<SchedulingDashboard> {
    const queryParams = new URLSearchParams();
    if (params?.organization_id) queryParams.set('organization_id', params.organization_id);
    if (params?.site_id) queryParams.set('site_id', params.site_id);

    const response = await apiClient.get<ApiResponse<SchedulingDashboard>>(
      `/api/scheduling/dashboard/now-playing?${queryParams.toString()}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch dashboard');
    return response.data.data;
  }

  static async getZoneWeekSchedule(zoneId: string): Promise<WeekSchedule> {
    const response = await apiClient.get<ApiResponse<WeekSchedule>>(
      `/api/scheduling/dashboard/zones/${zoneId}/week`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch week schedule');
    return response.data.data;
  }
}
